'use client'

import { useQuery } from '@apollo/client/react'
import { DialogDismiss, Focusable } from '@ariakit/react'
import { ComponentProps, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDebounce } from 'react-use'
import styled from 'styled-components'

import { CloseIcon, MediaAsContents } from '@syconium/little-miss-figgy'
import { IconButton } from '@syconium/little-miss-figgy/dist/components/IconButton/IconButton'
import { Search } from '@syconium/little-miss-figgy/dist/components/Icons/Icon/Search'
import { ScreenReaderOnly } from '@syconium/little-miss-figgy/dist/components/ScreenReaderOnly'
import { TextLink } from '@syconium/little-miss-figgy/dist/components/TextLink'
import {
  TypeStyle,
  TypeStyleBodyLarge,
  TypeStyleBodyMicro,
  TypeStyleHeadlineExtraExtraSmall,
} from '@syconium/little-miss-figgy/dist/components/TypeStyle'
import { fromMd, untilMd } from '@syconium/little-miss-figgy/dist/constants/breakpoints'
import { timingDefault } from '@syconium/little-miss-figgy/dist/constants/timing'
import { useIsViewport } from '@syconium/little-miss-figgy/dist/lib/hooks/useIsViewport'
import { ViewportSizes } from '@syconium/little-miss-figgy/dist/lib/hooks/useViewportBreakpoints'

import { gql } from '../../../__generated__/graphql/catalog/gql'
import { useGoogleTagManager } from '../../../lib/hooks/useGoogleTagManager/useGoogleTagManager'
import { useLocalization } from '../../_providers/LocalizationProvider.client'
import {
  useTrackProductInteraction,
  useTrackSearchResults,
} from '../../_providers/TrackingProvider.client'
import { useTranslation } from '../../_providers/TranslationProvider.client'
import { isDefinedAndNotNull } from '../../_utils/isDefinedAndNotNull'
import { NextLink } from '../navigation/NextLink'
import { PricingRange, derivePricingForVariantGroup } from '../pricing/PricingRange.client'
import { TextHighlighting } from '../text/TextHighlighting.client'
import {
  ProductTileContentHorizontalLayout,
  ProductTileImage,
  ProductTileImageSkeleton,
  ProductTilePriceSkeleton,
  ProductTileTitleSkeleton,
} from '../tiles/ProductTile.client'
import { Tile } from '../tiles/Tiles.client'

import { primaryNavHeaderMinHeightPixels } from './PrimaryNavHeader.client'
import { PrimaryNavMenuDialog, PrimaryNavMenuDialogTrigger } from './PrimaryNavMenus.client'

gql(`
  fragment NavSearchVariantGroup on ProductCatalogVariantGroup {
    id
    title
    seoTitle
    urlPath
    availability
    primaryCategory {
      id
      key
      displayValue
    }
    gender
    exclusive
    priceRange {
      currencyCode
      min {
        amount
        displayValue
      }
      max {
        amount
        displayValue
      }
    }
    compareAtPriceRange {
      min {
        amount
        displayValue
      }
      max {
        amount
        displayValue
      }
      minPercentageOff
      maxPercentageOff
    }
    color {
      id
    }
    images(first: 1) {
      url
      altText
    }
    product {
      id
      handle
    }
    defaultShopifyProduct {
      id
      defaultShopifyVariant {
        id
        sku
      }
    }
  }
`)

export const navSearchAutocompleteQuery = gql(`
  query PrimaryNavSearchAutocomplete($first: Int!, $query: String!) {
    search(query: $query) {
      suggestions {
        query
      }
      items(first: $first) {
        numItems
        nodes {
          productInfo {
            id
            group {
              ...NavSearchVariantGroup
            }
          }
        }
      }
    }
  }
`)

type StyledSearchMenuButtonContentProps = {
  children?: React.ReactNode
  className?: string
  searchText: string
}

const StyledSearchMenuButtonContent = styled(
  ({ searchText, ...props }: StyledSearchMenuButtonContentProps) => {
    return (
      <div {...props}>
        <Search />
        {searchText}
      </div>
    )
  }
)`
  align-items: center;
  border-radius: 4px;
  border: 1px solid ${o => o.theme.color.input.empty.border.on.background};
  background-color: ${o => o.theme.color.input.empty.fill.on.background};
  color: ${o => o.theme.color.input.empty.content.on.background};
  box-sizing: border-box;
  display: flex;
  font-size: 13px;
  line-height: 14px;
  font-weight: 600;
  padding: ${o => o.theme.spacing(0.5)} ${o => o.theme.spacing(8)} ${o => o.theme.spacing(0.5)}
    ${o => o.theme.spacing(2)};
  gap: ${o => o.theme.spacing(1)};

  &:hover,
  &:focus {
    border-color: ${o => o.theme.color.input.filled.border.on.background};
  }

  ${untilMd} {
    width: 100%;
    border-radius: 0px;
    border-width: 0px 0px 1px 0px;
    font-family: ${o => o.theme.typography.deprecated.font.family.primary};
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.6px;
    line-height: 18px;
    color: ${o => o.theme.color.input.empty.content.on.background};
    margin-bottom: ${o => o.theme.spacing(4)};
  }
`

export const PrimaryNavSearchMenuDialogTrigger = styled(
  ({
    children,
    searchText,
    ...props
  }: ComponentProps<typeof PrimaryNavMenuDialogTrigger> & StyledSearchMenuButtonContentProps) => {
    return (
      <PrimaryNavMenuDialogTrigger {...props}>
        <StyledSearchMenuButtonContent searchText={searchText}>
          {children}
        </StyledSearchMenuButtonContent>
      </PrimaryNavMenuDialogTrigger>
    )
  }
)`
  ${untilMd} {
    width: 100%;
  }
`

export const PrimaryNavSearchMenuDialog = ({
  gutter,
  ...props
}: ComponentProps<typeof PrimaryNavMenuDialog>) => {
  const isMediumOrLargeViewport = useIsViewport([ViewportSizes.medium, ViewportSizes.large], {
    ssr: false,
  })
  const desiredGutter =
    gutter ?? (!isMediumOrLargeViewport ? -primaryNavHeaderMinHeightPixels : undefined)
  return <PrimaryNavMenuDialog gutter={desiredGutter} {...props} />
}

export const PrimaryNavSearchMenuBody = styled.div`
  box-sizing: border-box;
  max-width: 993px;
  margin: 0px auto;
  display: block;
  width: 100%;

  ${fromMd} {
    width: 80%;
  }
`

const PrimaryNavSearchInputWrapper = styled.div<{
  $isInvalid: boolean
}>`
  display: flex;
  position: relative;
  gap: ${o => o.theme.spacing(2)};
  align-items: center;
  border-bottom: 1px solid ${o => o.theme.color.input.filled.border.on.background};
  border-bottom-color: ${o =>
    o.$isInvalid && o.theme.color.input.invalid.filled.border.on.background};
  transition: border-color ${timingDefault} ease;

  margin: ${o => o.theme.spacing(4, 0)};
  ${fromMd} {
    margin: 32px 0px 56px;
    border-bottom-width: 2px;
  }
`

const PrimaryNavSearchInput = styled.input`
  border: none;
  box-sizing: border-box;
  font-family: ${o => o.theme.typography.deprecated.font.family.primary};
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.6px;
  line-height: 18px;
  outline: none;
  width: 100%;

  ${fromMd} {
    font-size: 20px;
    padding: 0 32px;
  }
  ${TypeStyle.css.bodyMedium};
  color: ${o => o.theme.color.input.filled.content.on.background};
  ::placeholder {
    color: ${o => o.theme.color.input.empty.content.on.background};
  }
`

const PrimaryNavSearchSubmit = styled(
  ({
    flushBottom = true,
    flushLeft = true,
    flushRight = true,
    flushTop = true,
    type = 'submit',
    variant = 'black-on-transparent',
    borderless = true,
    ariaLabel,
    ...props
  }: ComponentProps<typeof IconButton>) => {
    return (
      <IconButton
        flushBottom={flushBottom}
        flushLeft={flushLeft}
        flushRight={flushRight}
        flushTop={flushTop}
        type={type}
        variant={variant}
        borderless={borderless}
        ariaLabel={ariaLabel}
        {...props}
      />
    )
  }
)`
  padding: 0;
`

const StyledNoResultsText = styled(TypeStyleBodyLarge)`
  margin: 42px 0;
  text-align: center;
  font-size: 20px;
  font-weight: bold;
`

const StyledSearchResultsGrid = styled.ul`
  display: grid;

  gap: 16px;
  grid-template-columns: 1fr;
  margin: 20px 0px 8px;
  ${fromMd} {
    gap: 36px;
    grid-template-columns: 1fr 1fr;
    margin: 56px 0px 16px;
  }
`

const StyledSearchResultsSuggestions = styled.div`
  border-top: 2px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  margin: 16px 32px 28px;

  margin: 24px 0px 0px;
  padding: 24px 0 36px;
  ${fromMd} {
    border: none;
    padding: 28px 0 56px;
    margin: 28px 48px 0px;
    text-align: center;
  }
`

const StyledSearchResultsSuggestionsTitle = styled(
  ({
    as = 'h3',
    isUppercase = true,
    color = 'secondary',
    ...props
  }: ComponentProps<typeof TypeStyleBodyMicro>) => {
    return <TypeStyleBodyMicro color={color} as={as} isUppercase={isUppercase} {...props} />
  }
)`
  margin-bottom: 20px;
  text-transform: uppercase;
`

type PrimaryNavSearchFormProps = {
  searchText: string
  noResultsText: string
  suggestedSearchesTitleText: string
  numberOfResultsText: string
  previewSearchResultsOnType: boolean
}

export const PrimaryNavSearchForm = ({
  searchText,
  noResultsText,
  suggestedSearchesTitleText,
  numberOfResultsText,
  previewSearchResultsOnType,
}: PrimaryNavSearchFormProps) => {
  const translations = useTranslation()
  const { region } = useLocalization()
  const { pushSearchSubmitted } = useGoogleTagManager()
  const { trackProductInteraction } = useTrackProductInteraction()
  const { trackSearchResults } = useTrackSearchResults()
  const [searchInputValue, setSearchInputValue] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState<string>(searchInputValue)
  const [getSearchQueryDebounceStatus] = useDebounce(
    () => {
      setSearchQuery(searchInputValue)
    },
    300,
    [searchInputValue]
  )
  const debouncing = getSearchQueryDebounceStatus() === false && searchQuery !== searchInputValue

  const { data, loading } = useQuery(navSearchAutocompleteQuery, {
    variables: {
      query: searchQuery,
      first: 6,
    },
    skip: searchQuery.length === 0,
  })

  const suggestedQueries = useMemo(() => {
    return data?.search.suggestions.map(suggestionNode => suggestionNode.query) ?? []
  }, [data?.search.suggestions])

  const unfilteredVariantGroups = useMemo(() => {
    return (data?.search.items.nodes ?? [])
      .map(node => node.productInfo.group)
      .filter(isDefinedAndNotNull)
  }, [data?.search.items.nodes])

  const variantGroups = useMemo(() => {
    return unfilteredVariantGroups.filter(
      unfilteredVariantGroup =>
        region.id === 'US' || unfilteredVariantGroup.primaryCategory.key !== 'GIFT_CARD'
    )
  }, [region.id, unfilteredVariantGroups])
  const skeletonProducts = useMemo(() => [0, 1, 2, 3, 4, 5], [])

  const status = useMemo(() => {
    if (searchQuery.length === 0 && !debouncing) return 'idle'
    if (loading || debouncing) return 'pending'
    if (variantGroups.length > 0) return 'resolved'
    return 'no-results'
  }, [debouncing, loading, variantGroups.length, searchQuery.length])

  const onFormSubmit = useCallback(() => {
    pushSearchSubmitted(searchInputValue)
  }, [pushSearchSubmitted, searchInputValue])

  const searchQueryTracker = useRef('')
  useEffect(() => {
    if (
      (status === 'resolved' || status === 'no-results') &&
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
      searchQueryTracker.current !== searchQuery
    ) {
      trackSearchResults({
        id: `header_`,
        query: searchQuery,
        searchType: 'header',
        topResults: variantGroups.map(variantGroup => {
          return {
            handle: variantGroup.product.handle,
            id: variantGroup.id,
            color: variantGroup.color?.id,
            shopifyId: variantGroup.defaultShopifyProduct.id,
            soldOut: variantGroup.availability === 'OUT_OF_STOCK',
          }
        }),
        totalResults: variantGroups.length,
      })
      searchQueryTracker.current = searchQuery
    }
  }, [trackSearchResults, searchQuery, status, variantGroups])

  return (
    <>
      <form autoComplete='off' noValidate role='search' action='/search' onSubmit={onFormSubmit}>
        <PrimaryNavSearchInputWrapper $isInvalid={false}>
          <PrimaryNavSearchSubmit aria-label={searchText} size='small' flushLeft>
            <Search aria-hidden='true' />
          </PrimaryNavSearchSubmit>
          <Focusable
            focusable
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            render={
              <PrimaryNavSearchInput
                aria-label={searchText}
                name='q'
                placeholder={searchText}
                value={searchInputValue}
                onChange={event => {
                  setSearchInputValue(event.target.value)
                }}
              />
            }
          />
          <MediaAsContents lessThan={'md'}>
            <StyledPrimarySearchNavMenuDialogDismiss
              aria-label={translations.magnolia.general.close}
            />
          </MediaAsContents>
        </PrimaryNavSearchInputWrapper>
      </form>

      {previewSearchResultsOnType ? (
        <>
          <ScreenReaderOnly>
            <div role='status' aria-live='polite' aria-atomic='true' aria-relevant='all'>
              {status === 'resolved' && typeof data?.search.items.numItems === 'number'
                ? numberOfResultsText.replace('{numberOfResults}', `${data.search.items.numItems}`)
                : status === 'no-results'
                ? noResultsText.replace('{searchText}', searchQuery)
                : null}
            </div>
          </ScreenReaderOnly>

          {status === 'no-results' ? (
            <StyledNoResultsText asTag='div'>
              {noResultsText.replace('{searchText}', searchQuery)}
            </StyledNoResultsText>
          ) : status === 'pending' ? (
            <StyledSearchResultsGrid as='ul' aria-label={translations.magnolia.general.loading}>
              {skeletonProducts.map((_, index) => {
                return (
                  <Tile as='li' key={index}>
                    <ProductTileContentHorizontalLayout>
                      <div style={{ width: '80px', flexShrink: 0, flexGrow: 0 }}>
                        <ProductTileImageSkeleton aspectRatio={'square'} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <ProductTileTitleSkeleton />
                        <ProductTilePriceSkeleton />
                      </div>
                    </ProductTileContentHorizontalLayout>
                  </Tile>
                )
              })}
            </StyledSearchResultsGrid>
          ) : status === 'resolved' ? (
            <>
              <StyledSearchResultsGrid as='ul' aria-label={translations.search.resultsListTitle}>
                {variantGroups.map((variantGroup, index) => {
                  const pricing = derivePricingForVariantGroup(variantGroup)
                  const imageSrc = variantGroup.images?.[0]?.url
                  return (
                    <Tile as='li' key={`${variantGroup.id}${index}`}>
                      <NextLink
                        href={variantGroup.urlPath}
                        onClick={() => {
                          trackProductInteraction({
                            handle: variantGroup.product.handle,
                            color: variantGroup.color?.id,
                            id: variantGroup.id,
                            shopifyId: variantGroup.defaultShopifyProduct.id,
                            soldOut: variantGroup.availability === 'OUT_OF_STOCK',
                          })
                        }}
                      >
                        <ProductTileContentHorizontalLayout>
                          <div style={{ width: '80px', flexShrink: 0, flexGrow: 0 }}>
                            {imageSrc ? (
                              <ProductTileImage
                                alt={variantGroup.images?.[0]?.altText || variantGroup.seoTitle}
                                loading='lazy'
                                src={imageSrc}
                                aspectRatio={'square'}
                                widths={{
                                  unit: 'px',
                                  sm: 80,
                                  md: 80,
                                }}
                              />
                            ) : null}
                          </div>
                          <div style={{ flex: 1 }}>
                            <TypeStyleBodyMicro color='primary'>
                              <TextHighlighting
                                text={variantGroup.seoTitle}
                                targetText={searchQuery}
                              />
                            </TypeStyleBodyMicro>
                            <TypeStyleBodyMicro color='primary'>
                              <PricingRange {...pricing} />
                            </TypeStyleBodyMicro>
                          </div>
                        </ProductTileContentHorizontalLayout>
                      </NextLink>
                    </Tile>
                  )
                })}
              </StyledSearchResultsGrid>
              {suggestedQueries.length > 0 ? (
                <StyledSearchResultsSuggestions>
                  <StyledSearchResultsSuggestionsTitle>
                    {suggestedSearchesTitleText}
                  </StyledSearchResultsSuggestionsTitle>
                  <ul>
                    {suggestedQueries.map((query, index) => (
                      <TypeStyleHeadlineExtraExtraSmall as='li' key={index}>
                        <TextLink
                          variant='underline-on-hover'
                          inheritTypography
                          as={NextLink}
                          href={`/search?q=${query}`}
                        >
                          {query}
                        </TextLink>
                      </TypeStyleHeadlineExtraExtraSmall>
                    ))}
                  </ul>
                </StyledSearchResultsSuggestions>
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
    </>
  )
}
export const StyledPrimarySearchNavMenuDialogDismiss = styled(
  ({ flushRight = true, ...props }: ComponentProps<typeof PopoverDialogDismiss>) => (
    <PopoverDialogDismiss flushRight={flushRight} {...props} />
  )
)`
  ${fromMd} {
    right: ${o => o.theme.dimensions.layout.sidePadding.md * 2}px;
    top: -${o => o.theme.dimensions.layout.sidePadding.md / 2}px;
  }

  background: ${o => o.theme.color.fill.strong};
`

const PopoverDialogDismiss = ({
  forwardedAs = DialogDismiss,
  borderless = true,
  ariaLabel,
  ...props
}: ComponentProps<typeof IconButton>) => (
  <ClickArea>
    <StyledIconButton
      size='small'
      forwardedAs={forwardedAs}
      variant={'white-on-black'}
      borderless={borderless}
      {...props}
      ariaLabel={ariaLabel}
    >
      <CloseIcon height={'3.5px'} width={'3.5px'} />
    </StyledIconButton>
  </ClickArea>
)

const StyledIconButton = styled(IconButton)`
  > svg {
    min-height: 8px;
    min-width: 8px;
  }
  min-height: 16px;
  min-width: 16px;
  border: none;
  padding: ${o => o.theme.spacing(1)};
  margin: 0;
`

const ClickArea = styled.div`
  padding: 4px;
  display: inline-flex;
  cursor: pointer;
  > * {
    pointer-events: auto;
  }
`
